import { Check, Minus, Shield, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { PERMISSIONS } from '../../../../permissions'

interface UserPermissionsTabProps {
    mode: 'create' | 'edit' | 'view'
    getPermissionState: (key: string) => 'allow' | 'deny' | 'inherit'
    isAllowedByRole: (key: string) => boolean
    updatePermission: (key: string, type: 'allow' | 'deny' | 'inherit') => void
}

export const UserPermissionsTab = ({
    mode,
    getPermissionState,
    isAllowedByRole,
    updatePermission,
}: UserPermissionsTabProps) => {
    return (
        <div className="space-y-4">
            <div className="space-y-2 rounded-lg border border-border bg-subdued p-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-ink">
                    <Shield className="size-4" />
                    Permission overrides
                </h4>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <div className="flex items-center gap-1.5 text-text-2">
                        <Minus className="size-4" /> Inherit
                    </div>
                    <div className="flex items-center gap-1.5 text-success-dark">
                        <Check className="size-4" /> Allowed
                    </div>
                    <div className="flex items-center gap-1.5 text-error-dark">
                        <X className="size-4" /> Denied
                    </div>
                </div>
                <p className="text-sm text-text-2">
                    Overrides take precedence over role permissions. Use "Inherit" to follow the assigned roles.
                </p>
            </div>

            <div className="space-y-4">
                {PERMISSIONS.map((group) => (
                    <div key={group.module} className="rounded-lg border border-border">
                        <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-4 py-3">
                            <Label className="text-sm font-semibold capitalize text-ink">
                                {group.module.replace(/_/g, ' ')}
                            </Label>
                            <Badge tone="soft">
                                {group.actions.length} {group.actions.length === 1 ? 'action' : 'actions'}
                            </Badge>
                        </div>

                        <div className="divide-y divide-border-subtle">
                            {group.actions.map((action) => {
                                const key = `${group.module}:${action}`
                                const state = getPermissionState(key)
                                const roleAllowed = isAllowedByRole(key)

                                return (
                                    <div
                                        key={key}
                                        className="flex items-center justify-between gap-3 px-4 py-2"
                                    >
                                        <div className="flex flex-col">
                                            <span className="text-sm capitalize text-ink">
                                                {action.replace(/_/g, ' ')}
                                            </span>
                                            {state === 'inherit' && (
                                                <span
                                                    className={`text-xs ${roleAllowed ? 'text-success-dark' : 'text-text-2'}`}
                                                >
                                                    {roleAllowed ? 'Allowed by role' : 'Denied by default'}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex shrink-0 items-center gap-0.5 rounded-md border border-border bg-subdued p-0.5">
                                            <Button
                                                type="button"
                                                variant={state === 'inherit' ? 'secondary' : 'ghost'}
                                                size="icon"
                                                className={cn(
                                                    "size-7 rounded-sm",
                                                    state === 'inherit'
                                                        ? "bg-card text-ink shadow-z1 hover:bg-card"
                                                        : "text-muted-foreground hover:bg-fill hover:text-ink"
                                                )}
                                                onClick={() => updatePermission(key, 'inherit')}
                                                disabled={mode === 'view'}
                                                title="Inherit from role"
                                                aria-label="Inherit from role"
                                            >
                                                <Minus />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={state === 'allow' ? 'default' : 'ghost'}
                                                size="icon"
                                                className={cn(
                                                    "size-7 rounded-sm",
                                                    state === 'allow'
                                                        ? "bg-success-lighter text-success-dark hover:bg-success-lighter"
                                                        : "text-muted-foreground hover:bg-fill hover:text-success-dark"
                                                )}
                                                onClick={() => updatePermission(key, 'allow')}
                                                disabled={mode === 'view'}
                                                title="Allow"
                                                aria-label="Allow"
                                            >
                                                <Check />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={state === 'deny' ? 'destructive' : 'ghost'}
                                                size="icon"
                                                className={cn(
                                                    "size-7 rounded-sm",
                                                    state === 'deny'
                                                        ? "bg-error-lighter text-error-dark hover:bg-error-lighter"
                                                        : "text-muted-foreground hover:bg-fill hover:text-error-dark"
                                                )}
                                                onClick={() => updatePermission(key, 'deny')}
                                                disabled={mode === 'view'}
                                                title="Deny"
                                                aria-label="Deny"
                                            >
                                                <X />
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
